import React, { useEffect, useState } from "react";
import Card from "components/card";
import { Spinner, Dialog, DialogHeader, DialogBody, Button } from "@material-tailwind/react";
import PieChart from "components/charts/PieChart";
import { getProspectionClientStatsApi, getProspectionCultureStatsApi, getProspectionTypeCultureStatsApi, getProspectionSolutionsStatsApi, getProspectionConcurrencesStatsApi, getProspectionClientReportsApi } from "api/operations";
import { getAllTcpsApi } from "api/filtersData";
import { pdfProspectionGeneratorApi } from "api/pdf";
import { exportPieToPptx } from "./exportPieToPptx";

const ProspectionStats = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [clientStats, setClientStats] = useState({ labels: [], series: [] });
  const [cultureStats, setCultureStats] = useState({ labels: [], series: [] });
  const [typeCultureStats, setTypeCultureStats] = useState({ labels: [], series: [] });
  const [solutionsStats, setSolutionsStats] = useState({ labels: [], series: [] });
  const [concurrencesStats, setConcurrencesStats] = useState({ labels: [], series: [] });
  const [concurrenceType, setConcurrenceType] = useState("");
  const [concurrenceTCP, setConcurrenceTCP] = useState("");
  const [isLoadingConcurrences, setIsLoadingConcurrences] = useState(false);
  const [listOfAllTcp, setListOfAllTcp] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getUTCFullYear());
  const [TCP, setTCP] = useState("");
  const [isClientReportsModalOpen, setIsClientReportsModalOpen] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [clientReports, setClientReports] = useState([]);
  const [isLoadingClientReports, setIsLoadingClientReports] = useState(false);
  const [isExportingClientPptx, setIsExportingClientPptx] = useState(false);
  const [isExportingCulturePptx, setIsExportingCulturePptx] = useState(false);
  const [isExportingTypeCulturePptx, setIsExportingTypeCulturePptx] = useState(false);
  const [isExportingSolutionsPptx, setIsExportingSolutionsPptx] = useState(false);
  const [isExportingConcurrencesPptx, setIsExportingConcurrencesPptx] = useState(false);

  useEffect(() => {
    getAllTcpsApi()
      .then((res) => setListOfAllTcp(res))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    fetchClientStats();
  }, [currentYear, TCP]);

  const fetchClientStats = async () => {
    try {
      setIsLoading(true);
      const [clientData, cultureData, typeCultureData, solutionsData] = await Promise.all([
        getProspectionClientStatsApi(currentYear, TCP),
        getProspectionCultureStatsApi(currentYear, TCP),
        getProspectionTypeCultureStatsApi(currentYear, TCP),
        getProspectionSolutionsStatsApi(currentYear, TCP),
      ]);
      setClientStats(clientData);
      setCultureStats(cultureData);
      setTypeCultureStats(typeCultureData);
      setSolutionsStats(solutionsData);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenClientReports = async (clientName) => {
    setSelectedClientName(clientName);
    setIsClientReportsModalOpen(true);
    setIsLoadingClientReports(true);
    try {
      const data = await getProspectionClientReportsApi(currentYear, TCP, clientName);
      setClientReports(data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingClientReports(false);
    }
  };

  const handleCloseClientReports = () => {
    setIsClientReportsModalOpen(false);
    setSelectedClientName("");
    setClientReports([]);
  };

  const handleExportClientStatsPptx = async () => {
    try {
      setIsExportingClientPptx(true);
      const tcpName = TCP
        ? listOfAllTcp.find((el) => el._id === TCP)?.Name || TCP
        : "Tous";
      await exportPieToPptx({
        title: "Clients avec prospections validées",
        subtitle: `Année ${currentYear}  •  TCP : ${tcpName}`,
        labels: clientStats.labels,
        series: clientStats.series,
        fileName: `clients-prospections-validees-${currentYear}`,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsExportingClientPptx(false);
    }
  };

  const handleExportCultureStatsPptx = async () => {
    try {
      setIsExportingCulturePptx(true);
      const tcpName = TCP
        ? listOfAllTcp.find((el) => el._id === TCP)?.Name || TCP
        : "Tous";
      await exportPieToPptx({
        title: "Répartition par Culture",
        subtitle: `Année ${currentYear}  •  TCP : ${tcpName}`,
        labels: cultureStats.labels,
        series: cultureStats.series,
        fileName: `repartition-culture-${currentYear}`,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsExportingCulturePptx(false);
    }
  };

  const handleExportTypeCultureStatsPptx = async () => {
    try {
      setIsExportingTypeCulturePptx(true);
      const tcpName = TCP
        ? listOfAllTcp.find((el) => el._id === TCP)?.Name || TCP
        : "Tous";
      await exportPieToPptx({
        title: "Répartition par Type de culture",
        subtitle: `Année ${currentYear}  •  TCP : ${tcpName}`,
        labels: typeCultureStats.labels,
        series: typeCultureStats.series,
        fileName: `repartition-type-culture-${currentYear}`,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsExportingTypeCulturePptx(false);
    }
  };

  const handleExportSolutionsStatsPptx = async () => {
    try {
      setIsExportingSolutionsPptx(true);
      const tcpName = TCP
        ? listOfAllTcp.find((el) => el._id === TCP)?.Name || TCP
        : "Tous";
      await exportPieToPptx({
        title: "Répartition par Solutions proposées",
        subtitle: `Année ${currentYear}  •  TCP : ${tcpName}`,
        labels: solutionsStats.labels,
        series: solutionsStats.series,
        fileName: `repartition-solutions-proposees-${currentYear}`,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsExportingSolutionsPptx(false);
    }
  };

  const concurrenceTypeLabels = {
    prix: "Prix",
    societe: "Société",
    produit: "Produit",
  };

  const handleExportConcurrencesStatsPptx = async () => {
    try {
      setIsExportingConcurrencesPptx(true);
      const tcpName = concurrenceTCP
        ? listOfAllTcp.find((el) => el._id === concurrenceTCP)?.Name || concurrenceTCP
        : "Tous";
      const typeLabel = concurrenceType
        ? concurrenceTypeLabels[concurrenceType] || concurrenceType
        : "Tout";
      await exportPieToPptx({
        title: "Répartition par Concurrences",
        subtitle: `Année ${currentYear}  •  Type : ${typeLabel}  •  TCP : ${tcpName}`,
        labels: concurrencesStats.labels,
        series: concurrencesStats.series,
        chartLimit: concurrencesStats.labels.length,
        fileName: `repartition-concurrences-${currentYear}`,
      });
    } catch (err) {
      console.log(err);
    } finally {
      setIsExportingConcurrencesPptx(false);
    }
  };

  const handleDownloadProspectionReport = (reportId) => {
    const url = pdfProspectionGeneratorApi(reportId);
    window.open(url, "_blank");
  };

  useEffect(() => {
    fetchConcurrencesStats();
  }, [currentYear, concurrenceType, concurrenceTCP]);

  const fetchConcurrencesStats = async () => {
    try {
      setIsLoadingConcurrences(true);
      const data = await getProspectionConcurrencesStatsApi(currentYear, concurrenceTCP, concurrenceType);
      setConcurrencesStats(data);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingConcurrences(false);
    }
  };

  const StatsTable = ({ data, onCountClick }) => {
    const total = data.series.reduce((a, b) => a + b, 0);
    return (
      <div className="mt-4">
        <p className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
          Total : {total}
        </p>
        <div className="max-h-96 overflow-y-auto overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="sticky top-0 bg-white dark:bg-navy-800">
              <tr className="border-b border-gray-200 text-left dark:border-white/10">
                <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">#</th>
                <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Libellé</th>
                <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Nombre</th>
                <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">%</th>
              </tr>
            </thead>
            <tbody>
              {data.labels.map((label, index) => {
                const value = data.series[index];
                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return (
                  <tr key={index} className="border-b border-gray-100 dark:border-white/5">
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">{index + 1}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">{label}</td>
                    <td className="px-3 py-2 text-sm">
                      {onCountClick ? (
                        <button
                          type="button"
                          className="font-semibold text-brand-500 underline hover:text-brand-700 dark:text-brand-400"
                          onClick={() => onCountClick(label)}
                        >
                          {value}
                        </button>
                      ) : (
                        <span className="text-gray-700 dark:text-white">{value}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const getPieOptions = (labels) => ({
    chart: { type: "pie" },
    labels,
    legend: { position: "bottom" },
    tooltip: {
      enabled: true,
      theme: "dark",
      custom: function ({ series, seriesIndex, w }) {
        const label = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];
        const total = series.reduce((a, b) => a + b, 0);
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        return (
          '<div style="background:#000;color:#fff;padding:10px 15px;border-radius:8px;">' +
          '<div style="font-weight:600;margin-bottom:4px;">' + label + "</div>" +
          '<div style="font-size:18px;font-weight:bold;">' + value + " rapports</div>" +
          '<div style="color:#9CA3AF;font-weight:bold;">' + pct + "%</div>" +
          "</div>"
        );
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => val.toFixed(1) + "%",
      style: { fontSize: "13px", fontWeight: 500, colors: ["#000"] },
      background: {
        enabled: true,
        padding: 4,
        borderRadius: 6,
        borderWidth: 0,
        opacity: 0.9,
        dropShadow: { enabled: false },
      },
    },
  });

  return (
    <div className="mt-5 grid grid-cols-1 gap-5">
      {/* Filters */}
      <Card extra="w-full p-4">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <small className="text-sm font-medium text-gray-600 dark:text-white">
              Filtrer par TCP :
            </small>
            <select
              className="flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
              value={TCP}
              onChange={(e) => setTCP(e.target.value)}
            >
              <option value="">Tout</option>
              {listOfAllTcp.map((el) => (
                <option key={el._id} value={el._id}>
                  {el.Name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <small className="text-sm font-medium text-gray-600 dark:text-white">
              Année :
            </small>
            <input
              type="number"
              min="1900"
              max={new Date().getUTCFullYear()}
              step="1"
              className="flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
              value={currentYear}
              onChange={(e) => setCurrentYear(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Pie Chart 1 — Clients */}
      <Card extra="w-full p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">
            Clients avec prospections validées
          </h4>
          <Button
            size="sm"
            variant="gradient"
            color="cyan"
            className="rounded-full"
            disabled={
              isLoading || isExportingClientPptx || clientStats.series.length === 0
            }
            onClick={handleExportClientStatsPptx}
          >
            {isExportingClientPptx ? "Export..." : "Exporter PowerPoint"}
          </Button>
        </div>
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner className="h-12 w-12" />
          </div>
        ) : clientStats.series.length === 0 ? (
          <p className="py-10 text-center text-gray-500">Aucune donnée disponible</p>
        ) : (
          <>
            <PieChart
              options={getPieOptions(clientStats.labels.slice(0, 10))}
              series={clientStats.series.slice(0, 10)}
              height="400px"
            />
            <StatsTable data={clientStats} onCountClick={handleOpenClientReports} />
          </>
        )}
      </Card>

      {/* Pie Chart 2 — Culture */}
      <Card extra="w-full p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">
            Répartition par Culture
          </h4>
          <Button
            size="sm"
            variant="gradient"
            color="cyan"
            className="rounded-full"
            disabled={
              isLoading || isExportingCulturePptx || cultureStats.series.length === 0
            }
            onClick={handleExportCultureStatsPptx}
          >
            {isExportingCulturePptx ? "Export..." : "Exporter PowerPoint"}
          </Button>
        </div>
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner className="h-12 w-12" />
          </div>
        ) : cultureStats.series.length === 0 ? (
          <p className="py-10 text-center text-gray-500">Aucune donnée disponible</p>
        ) : (
          <>
            <PieChart
              options={getPieOptions(cultureStats.labels.slice(0, 10))}
              series={cultureStats.series.slice(0, 10)}
              height="400px"
            />
            <StatsTable data={cultureStats} />
          </>
        )}
      </Card>

      {/* Pie Chart 3 — Type de culture */}
      <Card extra="w-full p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">
            Répartition par Type de culture
          </h4>
          <Button
            size="sm"
            variant="gradient"
            color="cyan"
            className="rounded-full"
            disabled={
              isLoading ||
              isExportingTypeCulturePptx ||
              typeCultureStats.series.length === 0
            }
            onClick={handleExportTypeCultureStatsPptx}
          >
            {isExportingTypeCulturePptx ? "Export..." : "Exporter PowerPoint"}
          </Button>
        </div>
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner className="h-12 w-12" />
          </div>
        ) : typeCultureStats.series.length === 0 ? (
          <p className="py-10 text-center text-gray-500">Aucune donnée disponible</p>
        ) : (
          <>
            <PieChart
              options={getPieOptions(typeCultureStats.labels.slice(0, 10))}
              series={typeCultureStats.series.slice(0, 10)}
              height="400px"
            />
            <StatsTable data={typeCultureStats} />
          </>
        )}
      </Card>

      {/* Pie Chart 4 — Solutions proposées */}
      <Card extra="w-full p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">
            Répartition par Solutions proposées
          </h4>
          <Button
            size="sm"
            variant="gradient"
            color="cyan"
            className="rounded-full"
            disabled={
              isLoading || isExportingSolutionsPptx || solutionsStats.series.length === 0
            }
            onClick={handleExportSolutionsStatsPptx}
          >
            {isExportingSolutionsPptx ? "Export..." : "Exporter PowerPoint"}
          </Button>
        </div>
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner className="h-12 w-12" />
          </div>
        ) : solutionsStats.series.length === 0 ? (
          <p className="py-10 text-center text-gray-500">Aucune donnée disponible</p>
        ) : (
          <>
            <PieChart
              options={getPieOptions(solutionsStats.labels.slice(0, 10))}
              series={solutionsStats.series.slice(0, 10)}
              height="400px"
            />
            <StatsTable data={solutionsStats} />
          </>
        )}
      </Card>

      {/* Pie Chart 5 — Concurrences (own filters) */}
      <Card extra="w-full p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h4 className="text-lg font-bold text-navy-700 dark:text-white">
            Répartition par Concurrences
          </h4>
          <Button
            size="sm"
            variant="gradient"
            color="cyan"
            className="rounded-full"
            disabled={
              isLoadingConcurrences ||
              isExportingConcurrencesPptx ||
              concurrencesStats.series.length === 0
            }
            onClick={handleExportConcurrencesStatsPptx}
          >
            {isExportingConcurrencesPptx ? "Export..." : "Exporter PowerPoint"}
          </Button>
        </div>
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <small className="text-sm font-medium text-gray-600 dark:text-white">
              Type de concurrence :
            </small>
            <select
              className="flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
              value={concurrenceType}
              onChange={(e) => setConcurrenceType(e.target.value)}
            >
              <option value="">Tout</option>
              <option value="prix">Prix</option>
              <option value="societe">Société</option>
              <option value="produit">Produit</option>
            </select>
          </div>
          <div>
            <small className="text-sm font-medium text-gray-600 dark:text-white">
              Filtrer par TCP :
            </small>
            <select
              className="flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none"
              value={concurrenceTCP}
              onChange={(e) => setConcurrenceTCP(e.target.value)}
            >
              <option value="">Tout</option>
              {listOfAllTcp.map((el) => (
                <option key={el._id} value={el._id}>
                  {el.Name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isLoadingConcurrences ? (
          <div className="flex w-full items-center justify-center py-10">
            <Spinner className="h-12 w-12" />
          </div>
        ) : concurrencesStats.series.length === 0 ? (
          <p className="py-10 text-center text-gray-500">Aucune donnée disponible</p>
        ) : (
          <PieChart
            options={getPieOptions(concurrencesStats.labels)}
            series={concurrencesStats.series}
            height="400px"
          />
        )}
      </Card>

      {/* Modal — Prospection reports for a selected client */}
      <Dialog size="lg" open={isClientReportsModalOpen} handler={handleCloseClientReports}>
        <DialogHeader>
          <p className="w-full text-center">
            Prospections de « {selectedClientName} »
          </p>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] overflow-y-auto">
          {isLoadingClientReports ? (
            <div className="flex w-full items-center justify-center py-10">
              <Spinner className="h-12 w-12" />
            </div>
          ) : clientReports.length === 0 ? (
            <p className="py-10 text-center text-gray-500">Aucun rapport trouvé</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-gray-200 text-left dark:border-white/10">
                    <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Date</th>
                    <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Zone</th>
                    <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Culture</th>
                    <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Personne à contacter</th>
                    <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white">Suivi</th>
                    <th className="px-3 py-2 text-sm font-bold text-navy-700 dark:text-white"></th>
                  </tr>
                </thead>
                <tbody>
                  {clientReports.map((report) => (
                    <tr key={report._id} className="border-b border-gray-100 dark:border-white/5">
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">
                        {report.createdAt ? new Date(report.createdAt).toLocaleDateString() : "-"}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">
                        {report.Prospection?.zone || "-"}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">
                        {report.Prospection?.culture || "-"}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">
                        {report.Prospection?.personneContacte || "-"}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-white">
                        {report.Prospection?.isSuivi ? "Oui" : "Non"}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <Button
                          size="sm"
                          variant="gradient"
                          color="cyan"
                          className="rounded-full"
                          onClick={() => handleDownloadProspectionReport(report._id)}
                        >
                          Télécharger
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogBody>
      </Dialog>
    </div>
  );
};

export default ProspectionStats;
